{
  "pages": {
    "main": {
      "type": "main",
      "heading": "The DOGA inclusive design assessment",
      "description": "<p>The DOGA inclusive design assessment tool is a starting point for organisations to approach inclusive design on a strategic level. Designing your products, services and digital solutions for diversity and inclusion is strategically important as part of the overall efforts on DEI (diversity, equity and inclusion).</p> <p> The inclusive design assessment tool covers four dimensions of inclusive design: Products and services, design process, design team as well as company culture.</p> <p>It encourages organisations to critically look at their products and services from several perspectives and combine this with other enabling factors for DEI and inclusive design.</p> <p>It can be used to fuel the conversation about inclusive design, and/or as a means to measure how inclusive your organisation’s products and services, design teams, design processes and company culture. It will also identify what to focus on to improve.</p> <h2 style=\"font-size: 1.125rem; font-weight: 500; margin-bottom: 0.5rem;\">How does it work?</h2> <p>The assessment cover the four dimensions of DOGA´s inclusive design framework:</p> <ol> <li>Products and services</li> <li>Design process</li> <li>Design team</li> <li>Company culture</li> </ol> <p>18 statements will be presented to you, and each statement will be accompanied by supportive information. Based on this you will evaluate and score your organisation on a 5- point scale.</p> <h2 style=\"font-size: 1.125rem; font-weight: 500; margin-bottom: 0.5rem;\">How can we use the results?</h2> <p>The result of the assessment will be presented to you in a visual overview. This can be used to identify focus points to advance your organisation’s strategic inclusive design practices.</p> <p>This inclusive design assessment tool is developed by Lomi Agency for DOGA. The dimension about products and services is based on <a style=\"text-decoration: underline;\" href=\"https://www.universalscore.global/\" target=\"_blank\">Idean’s “The Universal Score”</a>.</p>",
      "nextButtonText": "Let's start",
      "nextPage": "questionGroup1"
    },
    "questionGroup1": {
      "type": "questionGroup",
      "heading": "Dimension 1: Products & services ",
      "description": "The first part of the assessment covers the dimension Products and services. This dimension consists of five elements and for each element there will be a statement. The elements in this dimension are based on the universal score, a tool by Idean. Choose 1 - 5 for each statement based on what best fits your organisation's products and services. You are encouraged to score all statements but this is not required to complete the assessment.",
      "altText": "Graphic illustration of one out of four dimensions within the inclusive design framework and its respective parts, presented in a quarter of a circle. The five parts of this dimension are: Mental wellbeing, Belonging, Physical needs, Neurodiversity and People first.",
      "questionsGroup": 0,
      "prevButtonText": "Back to home page",
      "nextButtonText": "Continue to dimension 2",
      "prevPage": "main",
      "nextPage": "questionGroup2"
    },
    "questionGroup2": {
      "type": "questionGroup",
      "heading": "Dimension 2: Design process",
      "description": "The second part covers the dimension Design process. This dimension consists of seven elements and for each element there will be a statement. Choose 1 - 5 based on what best reflects your organisation’s design process. You are encouraged to score all statements but this is not required to complete the assessment.",
      "altText": "Graphic illustration of one out of four dimensions within the inclusive design framework and its respective parts, presented in a quarter of a circle. The seven parts of this dimension are: Design brief, User research, Idea phase, UX design, Co-creation, User testing and Marketing &PR.",
      "questionsGroup": 1,
      "prevButtonText": "Back to dimension 1",
      "nextButtonText": "Continue to dimension 3",
      "prevPage": "questionGroup1",
      "nextPage": "questionGroup3"
    },
    "questionGroup3": {
      "type": "questionGroup",
      "heading": "Dimension 3: Design team",
      "description": "The third part covers the dimension Design team. This dimension consists of three elements and for each element there will be a statement. Choose 1 - 5 based on what best reflects your organisation’s design team. You are encouraged to score all statements but this is not required to complete the assessment.",
      "altText": "Graphic illustration of one out of four dimensions within the inclusive design framework and its respective parts, presented in a quarter of a circle. The three parts of this dimension are: Team diversity, Team knowledge and External resources.",
      "questionsGroup": 2,
      "prevButtonText": "Back to dimension 2",
      "nextButtonText": "Continue to dimension 4",
      "prevPage": "questionGroup2",
      "nextPage": "questionGroup4"
    },
    "questionGroup4": {
      "type": "questionGroup",
      "heading": "Dimension 4: Company culture",
      "description": "The forth part covers the dimension Company culture. This dimension consists of three elements and for each element there will be a statement. Choose 1 - 5 based on what best reflects your organisation’s company culture. You are encouraged to score all statements but this is not required to complete the assessment.",
      "altText": "Graphic illustration of one out of four dimensions within the inclusive design framework and its respective parts, presented in a quarter of a circle. The three parts of this dimension are: Vision and goals, Leadership and Partners.",
      "questionsGroup": 3,
      "prevButtonText": "Back to dimension 3",
      "nextButtonText": "Continue to results",
      "prevPage": "questionGroup3",
      "nextPage": "result"
    },
    "result": {
      "type": "result",
      "heading": "Results",
     "description": "<p>You have completed the DOGA inclusive design assessment! The results from each of the questions are visualised in the illustration above. At the bottom of this page, the scores are displayed per dimension for a more detailed overview.</p> <h2 style=\"font-size: 1.125rem; font-weight: 500; margin-bottom: 0.5rem;\">Interpret the results</h2><p>Each coloured block represents the score points that are based on your answer on each of the statements. If you have selected &quot;I don’t know/Not applicable&quot;, the blocks for that statement are blanked out.</p><p>The inner circle represents the minimum score (1). Each block towards the outer side of the circle reflects the score point(s) on top of this (score 2 to 5).</p><p>The results cannot be saved online, so please press the button below to print the result or download as a PDF.</p> <h2 style=\"font-size: 1.125rem; font-weight: 500; margin-bottom: 0.5rem;\">How can you use the results?</h2> <p>The results can be used as a starting point to identify which aspects your organisation is doing well, and what it can improve when it comes to practicing strategic inclusive design.<p>You can now start the process of creating your strategy for inclusive design.</p><p>To support you in this process, DOGA have developed an inclusive design change guide.</p><a href=\"https://doga.no/en/tools/inclusive-design/tools-and-methods/inclusive-design-change-guide\" target=\"_blank\" style=\"text-decoration: underline;\">You can access the change guide here (doga.no)</a>",
      "prevButtonText": "Back to dimension 4",
      "prevPage": "questionGroup4"
    }
  },
  "questionGroup": [
    {
      "title": "Products & services",
      "color":  "turquoise",
      "questions": [
        {
          "title": "Mental wellbeing",
          "question": "Our products and services are designed for <strong>mental wellbeing</strong>",
          "bullets": [
            "The user has controll over the journey",
            "It is easy to fix mistakes",
            "Instructions are clear",
            "The products and/or services don’t create urgency"
          ]
        },
        {
          "title": "Belonging",
          "question": "Our products and services are designed for <strong>belonging</strong>.",
          "bullets": [
            "There is diverse representation in imagery",
            "The used language is clear to all",
            "Cultural considerations are taking into account",
            "Information sensitivity is taken into account"
          ]
        },
        {
          "title": "Physical needs",
          "question": "Our products and services are designed for <strong>diverse physical needs.</strong>",
          "bullets": [
            "Visual impairments are taken into account",
            "Hearing impairments are taken into account",
            "Physical impairments are taken into account",
            "Digital support is easily accessible"
          ]
        },
        {
          "title": "Neurodiversity",
          "question": "Our products and services are designed for <strong>neurodiversity</strong>.",
          "bullets": [
            "Minimal distractions allowing for focus on the task at hand",
            "There is a possibility to save progress and continue later",
            "Tasks are broken down in smaller pieces",
            "Avoids users making impulsive decisions"
          ]
        },
        {
          "title": "People first",
          "question": "Our products and services are designed with a <strong>\"people first\" mentality</strong>.",
          "bullets": [
            "Decisions in the user’s best interests are encouraged",
            "Enough information is provided for informed decisions making",
            "Information is provided to the user timely"
          ]
        }
      ]
    },
    {
      "title": "Design process",
      "color":  "orange",
      "questions": [
        {
          "title": "Design brief",
          "question": "Diversity, equity and inclusion (DEI) is taken into account <strong>when composing the design brief</strong>.",
          "bullets": [
            "The relevance of DEI in the project is explored and covered in the brief",
            "The owner of the brief is challenged with regards to DEI to allow for sufficient resources"
          ]
        },
        {
          "title": "User research",
          "question": "Diversity, equity and inclusion (DEI) is taken into account during <strong>user research</strong>.",
          "bullets": [
            "The relevance of DEI in user research is defined",
            "User research participants reflect DEI goals within the project"
          ]
        },
        {
          "title": "Idea phase",
          "question": "Diversity, equity and inclusion (DEI) is taken into account during <strong>the ideation phase</strong>.",
          "bullets": [
            "Ideation is actively shaped by a diversity of users",
            "Ideation is actively shaped by a diversity of contexts"
          ]
        },
        {
          "title": "Co-creation",
          "question": "Diversity, equity and inclusion (DEI) is taken into account during <strong>co-creation</strong>.",
          "bullets": [
            "The relevance of DEI in co-creation is defined",
            "Co-creation participants reflect DEI goals within the project"
          ]
        },
        {
          "title": "UX design",
          "question": "Diversity, equity and inclusion (DEI) is taken into account during <strong>user experience (UX) design</strong>.",
          "bullets": [
            "Requirements are set for an equitable experience for a diversity of users.",
            "DEI goals are reflected at every touchpoint."
          ]
        },
        {
          "title": "User testing",
          "question": "Diversity, equity and inclusion (DEI) is taken into account during <strong>user testing</strong>.",
          "bullets": [
            "The relevance of DEI in user research is defined",
            "User testing participants reflect DEI goals within the project"
          ]
        },
        {
          "title": "Marketing & PR",
          "question": "Diversity, equity and inclusion (DEI) is taken into account during <strong>marketing & PR</strong>.",
          "bullets": [
            "Shown imagery follows DEI goals for the project",
            "Used language follows DEI goals for the project",
            "Conveyed messages follow DEI goals for the project"
          ]
        }
      ]
    },
    {
      "title": "Design team",
      "color":  "blue",
      "questions": [
        {
          "title": "Team diversity",
          "question": "Our design teams are <strong>diverse across several aspects</strong>. ",
          "bullets": [
            "Think of: gender, cultural, educational/professional background, age, cognitive traits and physical traits.,"
          ]
        },
        {
          "title": "Team knowledge",
          "question": "Our design team members have <strong>knowledge of, and experience with</strong> diversity, equity and inclusion (DEI) and inclusive design.",
          "bullets": [
            "Team members have had training with regards to DEI and inclusive design",
            "Team members have experience working with DEI principles and inclusive design"
          ]
        },
        {
          "title": "External resources",
          "question": "Our design teams have <strong>access to external resources</strong> to complement their knowledge of, and experience with diversity, equity and inclusion (DEI) and inclusive design.",
          "bullets": [
            "Think of: Experts, mentors, partners and users."
          ]
        }
      ]
    },
    {
      "title": "Company culture",
      "color":  "red",
      "questions": [
        {
          "title": "Vision and goals",
          "question": "Our company culture reflects commitment to diversity, equity and inclusion (DEI) through its <strong>vision and goals</strong>.",
          "bullets": [
            "DEI is part of the organisation’s vision",
            "There are clearly defined organisational goals with regards to DEI"
          ]
        },
        {
          "title": "Leadership",
          "question": "Our company culture reflects commitment to diversity, equity and inclusion (DEI) through its <strong>leadership</strong>. ",
          "bullets": [
            "Leadership is openly committed to DEI",
            "Leadership takes decisions in line with the organisation’s commitment to DEI",
            "Representation within leadership is in line with the organisation’s goals with regards to DEI"
          ]
        },
        {
          "title": "Partners",
          "question": "Our company culture reflects commitment to diversity, equity and inclusion (DEI) through its <strong>partners</strong>. ",
          "bullets": [
            "The organisations actively pursues cooperations with partners that are committed to DEI",
            "Diversity amongst partners reflects the organisation’s goals with regards to DEI"
          ]
        }
      ]
    }


  ],
  "answers": [
    {
      "value": 1,
      "name": "Overlooked",
      "description": "This element has no priority"
    },
    {
      "value": 2,
      "name": "Considered",
      "description": "This element is being discussed"
    },
    {
      "value": 3,
      "name": "Started",
      "description": "Action around this element has started"
    },
    {
      "value": 4,
      "name": "Structured",
      "description": "A plan is formulated around this element and actions are aligned."
    },
    {
      "value": 5,
      "name": "Integrated",
      "description": "Actions around this element are coordinated routines and accountability has been established."
    },
    {
      "value": 0,
      "name": "I don’t know/not applicable",
      "description": ""
    }
  ]

}